/**
 * useEmonTokenTransfer - SPL Token transfer for NFT purchases
 * 
 * Flow: Buyer sends EMON tokens directly to Seller's wallet using standard SPL Token Program.
 * No custom Anchor program needed - works immediately on mainnet.
 */
import { useCallback, useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Buffer } from "buffer";
import { PublicKey, Transaction } from "@solana/web3.js";
import {
  getAssociatedTokenAddressSync,
  createTransferInstruction,
  createAssociatedTokenAccountIdempotentInstruction,
} from "@solana/spl-token";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { createResilientConnection } from "@/integrations/solana/resilientConnection";
import { usdToEmon } from "@/integrations/anchor/emon-nft-bonding-idl";

// EMON Token Mint Address (mainnet)
const EMON_MINT = new PublicKey("76kfPfv218KhNQGx7gpC1GR9UWLZHBGSimhyK79ZpUMP");
const TOKEN_PROGRAM_ID = new PublicKey("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA");

// Treasury/Burn wallet for platform fee
const BURN_WALLET = new PublicKey("D3eP3tr9vGoMRZVXND82fTZFwh5vs7XWX24ZcbhEN8J");

// FIXED burn fee: exactly 100 EMON per sale (6 decimals) - NOT affected by price or rate
const BURN_FEE_EMON = 100_000_000n; // 100 EMON in smallest units

type SolanaRpcMethod =
  | "getTokenAccountsByOwner"
  | "getLatestBlockhash"
  | "sendTransaction"
  | "getSignatureStatuses";

async function solanaRpc<T>(method: SolanaRpcMethod, params: any[] = []): Promise<T> {
  const { data, error } = await supabase.functions.invoke("solana-rpc", {
    body: { method, params },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data.result as T;
}

async function getTokenBalanceByOwner(params: {
  owner: string;
  mint: string;
}): Promise<bigint> {
  const result = await solanaRpc<any>("getTokenAccountsByOwner", [
    params.owner,
    { mint: params.mint },
    { encoding: "jsonParsed", commitment: "confirmed" },
  ]);

  const accounts: any[] = Array.isArray(result?.value) ? result.value : [];
  let total = 0n;
  for (const a of accounts) {
    const amountStr =
      a?.account?.data?.parsed?.info?.tokenAmount?.amount ??
      a?.account?.data?.parsed?.info?.tokenAmount?.uiAmountString;
    if (typeof amountStr === "string" && amountStr.length > 0) {
      try {
        total += BigInt(amountStr);
      } catch {
        // ignore
      }
    }
  }

  return total;
}

async function waitForSignatureConfirmed(signature: string, timeoutMs = 45_000) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const result = await solanaRpc<any>("getSignatureStatuses", [
      [signature],
      { searchTransactionHistory: true },
    ]);

    const status = result?.value?.[0];

    if (status?.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
    }

    const cs = status?.confirmationStatus;
    if (cs === "confirmed" || cs === "finalized") {
      return;
    }

    await new Promise((r) => setTimeout(r, 1500));
  }

  throw new Error("Transaction confirmation timed out. Please try again.");
}


interface FraudCheckResult {
  allowed: boolean;
  decision: "allowed" | "blocked" | "flagged" | "error";
  risk_score: number;
  risk_factors: string[];
  message: string;
}

export interface NftSaleEvent {
  nftId: string;
  buyer: string;
  seller: string;
  priceUsd: number;
  priceEmon: number;
  signature: string;
  timestamp: Date;
}

export type BuyNftResult = {
  signature: string;
  newPriceUsd: number;
  synced: boolean;
  syncError?: string;
};

export function useEmonTokenTransfer() {
  const connection = useMemo(
    () => createResilientConnection({ commitment: "confirmed" }),
    [],
  );
  const { publicKey, signTransaction, sendTransaction, connected, signMessage } = useWalletSafe();
  const { fingerprint } = useDeviceFingerprint();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [recentSales, setRecentSales] = useState<NftSaleEvent[]>([]);

  // Fraud check (same as before)
  const performFraudCheck = useCallback(
    async (
      buyerWallet: string,
      sellerWallet: string,
      nftId: string,
      priceUsd?: number,
    ): Promise<FraudCheckResult> => {
      if (!fingerprint) {
        console.error("🚫 SECURITY: Fingerprint not available - BLOCKING transaction");
        return {
          allowed: false,
          decision: "blocked",
          risk_score: 100,
          risk_factors: ["fingerprint_unavailable"],
          message: "Security verification failed. Please refresh the page and try again.",
        };
      }

      try {
        const response = await supabase.functions.invoke("fraud-check", {
          body: {
            event_type: "nft_trade",
            wallet_address: buyerWallet,
            counterparty_wallet: sellerWallet,
            nft_id: nftId,
            price: priceUsd,
            fingerprint: {
              ip_address: fingerprint.ip_address,
              user_agent: fingerprint.user_agent,
              platform: fingerprint.platform,
              language: fingerprint.language,
              timezone: fingerprint.timezone,
              screen_resolution: fingerprint.screen_resolution,
              hardware_concurrency: fingerprint.hardware_concurrency,
              device_memory: fingerprint.device_memory,
              touch_support: fingerprint.touch_support,
              webgl_renderer: fingerprint.webgl_renderer,
              canvas_fingerprint: fingerprint.canvas_fingerprint,
              is_headless: fingerprint.is_headless,
              is_automated: fingerprint.is_automated,
              has_webdriver: fingerprint.has_webdriver,
              plugins_count: fingerprint.plugins_count,
              languages_count: fingerprint.languages_count,
              screen_depth_ratio: fingerprint.screen_depth_ratio,
              timing_anomaly: fingerprint.timing_anomaly,
              mouse_movements: fingerprint.mouse_movements,
              keyboard_events: fingerprint.keyboard_events,
              scroll_events: fingerprint.scroll_events,
              session_duration_ms: fingerprint.session_duration_ms,
            },
          },
        });

        if (response.error) {
          console.error("🚫 SECURITY: Fraud check error - BLOCKING transaction:", response.error);
          return {
            allowed: false,
            decision: "blocked",
            risk_score: 100,
            risk_factors: ["fraud_check_error"],
            message: "Security verification failed. Please try again later.",
          };
        }

        return response.data as FraudCheckResult;
      } catch (error) {
        console.error("🚫 SECURITY: Fraud check failed - BLOCKING transaction:", error);
        return {
          allowed: false,
          decision: "blocked",
          risk_score: 100,
          risk_factors: ["fraud_check_failed"],
          message: "Security verification failed. Please try again later.",
        };
      }
    },
    [fingerprint],
  );

  /**
   * Buy NFT by transferring EMON tokens from buyer to seller
   * @param nftId - NFT ID
   * @param sellerWalletAddress - Seller wallet address
   * @param currentPriceUsd - USD price
   * @param frozenEmonRate - Optional frozen EMON rate (if provided, uses this instead of live rate)
   */
  const buyNft = useCallback(
    async (
      nftId: string,
      sellerWalletAddress: string,
      currentPriceUsd: number,
      frozenEmonRate?: number,
    ): Promise<BuyNftResult | null> => {
      console.log("🛒 buyNft called:", { nftId, sellerWalletAddress, currentPriceUsd, frozenEmonRate });
      console.log("🔗 Wallet state:", { connected, publicKey: publicKey?.toBase58(), hasSignTransaction: !!signTransaction, hasSendTransaction: !!sendTransaction });

      if (!publicKey) {
        toast({ title: "Connect your wallet first", variant: "destructive" });
        return null;
      }
      
      if (!connected) {
        toast({ title: "Wallet not connected", description: "Please connect your wallet and try again.", variant: "destructive" });
        return null;
      }

      if (!signTransaction && !sendTransaction) {
        toast({ 
          title: "Wallet not ready", 
          description: "Your wallet doesn't support signing transactions. Try refreshing the page.", 
          variant: "destructive" 
        });
        return null;
      }

      const buyerWallet = publicKey.toBase58();

      // Block self-buying
      if (buyerWallet === sellerWalletAddress) {
        toast({
          title: "Cannot buy your own NFT",
          description: "Self-buying is not allowed.",
          variant: "destructive",
        });
        return null;
      }

      setLoading(true);

      // Fraud check
      console.log("🔍 Running fraud check...");
      const fraudCheck = await performFraudCheck(buyerWallet, sellerWalletAddress, nftId, currentPriceUsd);
      if (!fraudCheck.allowed) {
        setLoading(false);
        toast({
          title: "Transaction Blocked",
          description: fraudCheck.message || "Suspicious activity detected.",
          variant: "destructive",
        });
        return null;
      }

      if (fraudCheck.decision === "flagged") {
        console.warn(`⚠️ Fraud flagged (allowed): Score ${fraudCheck.risk_score}`);
      }

      try {
        const sellerPubkey = new PublicKey(sellerWalletAddress);

        // Calculate EMON amount from USD price using frozen rate if provided
        let emonAmount: number;
        if (frozenEmonRate && frozenEmonRate > 0) {
          // Use frozen rate for consistent pricing during transaction
          emonAmount = Math.floor((currentPriceUsd / frozenEmonRate) * 1_000_000); // smallest units
          console.log(`🔒 Using FROZEN rate: $${frozenEmonRate.toFixed(8)} → ${emonAmount} EMON smallest units`);
        } else {
          // Fallback to live rate
          emonAmount = usdToEmon(currentPriceUsd);
          console.log(`📊 Using LIVE rate → ${emonAmount} EMON smallest units`);
        }
        
        if (emonAmount <= 0) {
          throw new Error("Invalid EMON amount calculated. Please refresh and try again.");
        }


        console.log(`💰 Buying NFT for ${emonAmount} EMON ($${currentPriceUsd} USD)`);

        // Get buyer's EMON token account
        const buyerAta = getAssociatedTokenAddressSync(EMON_MINT, publicKey);
        console.log("📦 Buyer ATA:", buyerAta.toBase58());

        // Check buyer has enough EMON (server-side RPC avoids mobile CORS issues)
        let buyerBalance = 0n;
        try {
          console.log("📊 Checking buyer balance (backend RPC)...");
          buyerBalance = await getTokenBalanceByOwner({
            owner: buyerWallet,
            mint: EMON_MINT.toBase58(),
          });
          console.log("💳 Buyer balance:", buyerBalance.toString());
        } catch (e: any) {
          console.error("❌ Error checking balance:", e);
          toast({
            title: "Network Error",
            description: "Failed to check your token balance. Please try again.",
            variant: "destructive",
          });
          setLoading(false);
          return null;
        }

        if (buyerBalance === 0n) {
          toast({
            title: "No EMON tokens found",
            description: "You need EMON tokens to buy NFTs. Get some on Raydium first.",
            variant: "destructive",
          });
          setLoading(false);
          return null;
        }


        // Balance check moved below where we calculate total with fee

        // Get or create seller's EMON token account
        const sellerAta = getAssociatedTokenAddressSync(EMON_MINT, sellerPubkey);
        console.log("📦 Seller ATA:", sellerAta.toBase58());

        // Get or create burn wallet EMON token account
        const burnAta = getAssociatedTokenAddressSync(EMON_MINT, BURN_WALLET);
        console.log("🔥 Burn ATA:", burnAta.toBase58());

        const transaction = new Transaction();

        // Always add idempotent ATA creation for seller (safe if it already exists)
        transaction.add(
          createAssociatedTokenAccountIdempotentInstruction(
            publicKey, // payer
            sellerAta, // ata
            sellerPubkey, // owner
            EMON_MINT, // mint
          ),
        );

        // Always add idempotent ATA creation for burn wallet (safe if it already exists)
        transaction.add(
          createAssociatedTokenAccountIdempotentInstruction(
            publicKey, // payer
            burnAta, // ata
            BURN_WALLET, // owner
            EMON_MINT, // mint
          ),
        );

        // FEE ON TOP MODEL: Buyer pays priceEmon + 100 EMON fee
        // Seller receives: priceEmon (full price)
        // Burn receives: 100 EMON (fixed fee)
        const priceEmonAmount = BigInt(emonAmount);
        const totalEmonAmount = priceEmonAmount + BURN_FEE_EMON;
        
        console.log(`🔥 Fee on top: Price ${priceEmonAmount.toString()} + Fee ${BURN_FEE_EMON.toString()} = Total ${totalEmonAmount.toString()} EMON`);
        
        // Verify buyer has enough for price + fee
        if (buyerBalance < totalEmonAmount) {
          toast({
            title: "Insufficient EMON balance",
            description: `You need ${Number(totalEmonAmount).toLocaleString()} EMON (${emonAmount.toLocaleString()} + 100 fee) but have ${Number(buyerBalance).toLocaleString()}`,
            variant: "destructive",
          });
          setLoading(false);
          return null;
        }
        
        // Seller receives full price (fee is on top, not deducted)
        const sellerAmount = priceEmonAmount;
        
        console.log(`💰 Split: Seller ${sellerAmount.toString()} EMON, Burn ${BURN_FEE_EMON.toString()} EMON, Total ${totalEmonAmount.toString()} EMON`);

        // Add transfer to seller (full price - fee is separate)
        transaction.add(
          createTransferInstruction(
            buyerAta, // from
            sellerAta, // to
            publicKey, // owner
            sellerAmount, // full price amount
          ),
        );

        // Add transfer to burn wallet (fixed 100 EMON fee on top)
        transaction.add(
          createTransferInstruction(
            buyerAta, // from
            burnAta, // to
            publicKey, // owner
            BURN_FEE_EMON, // exactly 100 EMON
          ),
        );

        // Get blockhash (server-side RPC avoids mobile CORS issues)
        console.log("🔑 Getting blockhash (backend RPC)...");
        const latest = await solanaRpc<any>("getLatestBlockhash", [
          { commitment: "confirmed" },
        ]);
        const blockhash = latest?.value?.blockhash as string;
        const lastValidBlockHeight = latest?.value?.lastValidBlockHeight as number;

        if (!blockhash || !lastValidBlockHeight) {
          throw new Error("Failed to fetch latest blockhash. Please try again.");
        }

        transaction.recentBlockhash = blockhash;
        transaction.feePayer = publicKey;
        // @solana/web3.js supports this field; keep it for wallets that validate expiry
        (transaction as any).lastValidBlockHeight = lastValidBlockHeight;

        // Validate transaction before signing (Phantom compatibility)
        const { validateTransactionForPhantom } = await import("@/lib/phantomWarningMonitor");
        const validation = validateTransactionForPhantom(transaction);
        if (!validation.valid) {
          console.error("❌ Transaction validation failed:", validation.issues);
          throw new Error(`Transaction validation failed: ${validation.issues.join(", ")}`);
        }

        console.log("✅ Transaction validated for Phantom compatibility");
        console.log("✍️ Requesting wallet signature...");

        let signature: string;
        try {
          if (signTransaction) {
            console.log("📝 Using signTransaction + backend sendTransaction...");
            const signedTx = await signTransaction(transaction);
            const base64Tx = Buffer.from(signedTx.serialize()).toString("base64");

            signature = await solanaRpc<string>("sendTransaction", [
              base64Tx,
              {
                encoding: "base64",
                skipPreflight: false,
                preflightCommitment: "confirmed",
              },
            ]);
          } else if (sendTransaction) {
            console.log("📤 Using sendTransaction (wallet RPC)...");
            signature = await sendTransaction(transaction, connection);
          } else {
            throw new Error("No signing method available");
          }
        } catch (signError: any) {
          console.error("❌ Wallet signing error:", signError);

          // User rejected
          if (
            signError.message?.includes("rejected") ||
            signError.message?.includes("cancelled") ||
            signError.message?.includes("denied")
          ) {
            toast({
              title: "Transaction cancelled",
              description: "You cancelled the transaction in your wallet.",
              variant: "destructive",
            });
            setLoading(false);
            return null;
          }

          throw signError;
        }

        console.log(`📨 Transaction sent: ${signature}`);

        // Confirm transaction (backend RPC)
        console.log("⏳ Waiting for confirmation (backend RPC)...");
        await waitForSignatureConfirmed(signature);

        console.log(`✅ Transaction confirmed: ${signature}`);

        // Ensure we have a valid backend session bound to this wallet (needed for backend function calls)
        const ensureWalletSession = async () => {
          const { data: { session } } = await supabase.auth.getSession();
          const sessionWallet = session?.user?.user_metadata?.wallet_address as string | undefined;
          const expiresAt = session?.expires_at;

          if (session && sessionWallet === buyerWallet && expiresAt && expiresAt * 1000 > Date.now()) {
            return true;
          }

          if (!signMessage) {
            return false;
          }

          const { requestAuthNonce, verifySignature } = await import("@/lib/walletConnect");
          const nonceData = await requestAuthNonce(buyerWallet);
          if (!nonceData) return false;

          const encodedMessage = new TextEncoder().encode(nonceData.message);
          const signatureUint8 = await signMessage(encodedMessage);
          const bs58 = await import("bs58");
          const sig = bs58.default.encode(signatureUint8);

          const verifyResult = await verifySignature(buyerWallet, sig, nonceData.message);
          if (!verifyResult.success || !verifyResult.session) return false;

          await supabase.auth.setSession({
            access_token: verifyResult.session.access_token,
            refresh_token: verifyResult.session.refresh_token,
          });

          return true;
        };

        // Record sale in database via backend function (requires session)
        console.log("📝 Recording sale in database...");

        const sessionOk = await ensureWalletSession();
        if (!sessionOk) {
          toast({
            title: "Purchased, syncing…",
            description: "Transaction confirmed. Please reconnect your wallet to sync ownership & price.",
          });
        }

        // BACKEND AUTHORITY: Send only minimal data
        // Backend reads NFT price from DB, EMON rate from cache, calculates EMON amount
        const { data: saleResult, error: saleError } = await supabase.functions.invoke("record-nft-sale", {
          body: {
            nft_id: nftId,
            buyer_wallet: buyerWallet,
            tx_signature: signature,
            // DEPRECATED fields for backwards compatibility - backend ignores these
            seller_wallet: sellerWalletAddress,
            price_usd: currentPriceUsd,
          },
        });

        let synced = false;
        let syncError: string | undefined;

        if (saleError) {
          console.error("❌ Failed to record sale:", saleError);
          syncError = saleError.message || "Ownership/price sync failed.";
          toast({
            title: "🎉 NFT purchased (sync failed)",
            description: syncError + " Refresh and try again.",
            variant: "destructive",
          });
        } else if (!saleResult?.success) {
          console.error("❌ Sale recording failed:", saleResult?.error, saleResult?.details);
          syncError = saleResult?.details || saleResult?.error || "Ownership/price sync may be delayed.";
          toast({
            title: "🎉 NFT purchased (sync failed)",
            description: syncError,
            variant: "destructive",
          });
        } else {
          synced = true;
          console.log(`✅ Sale recorded: new price $${saleResult.new_price}`);
          toast({
            title: "🎉 NFT purchased!",
            description: `New price: $${saleResult.new_price.toFixed(2)} USD`,
          });
        }

        // Record sale event locally
        const saleEvent: NftSaleEvent = {
          nftId,
          buyer: buyerWallet,
          seller: sellerWalletAddress,
          priceUsd: currentPriceUsd,
          priceEmon: emonAmount,
          signature,
          timestamp: new Date(),
        };
        setRecentSales((prev) => [saleEvent, ...prev.slice(0, 49)]);

        // Invalidate NFT queries immediately so UI shows new price
        console.log("🔄 Invalidating marketplace queries...");
        await queryClient.invalidateQueries({ queryKey: ["marketplace-nfts"] });
        await queryClient.invalidateQueries({ queryKey: ["trending-nfts"] });
        await queryClient.invalidateQueries({ queryKey: ["nft", nftId] });

        return {
          signature,
          newPriceUsd: saleResult?.new_price || currentPriceUsd, // Fallback to current if backend failed
          synced,
          syncError,
        };
      } catch (error: any) {
        console.error("Buy NFT error:", error);
        toast({
          title: "Failed to buy NFT",
          description: error.message || "Transaction failed. Please try again.",
          variant: "destructive",
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [publicKey, signTransaction, sendTransaction, connected, connection, performFraudCheck, queryClient],
  );

  // Get Solana Explorer URL
  const getExplorerUrl = useCallback((addressOrSig: string, type: "address" | "tx" = "address"): string => {
    const base = "https://explorer.solana.com";
    return type === "tx" ? `${base}/tx/${addressOrSig}` : `${base}/address/${addressOrSig}`;
  }, []);

  return {
    loading,
    connected,
    publicKey,
    buyNft,
    recentSales,
    getExplorerUrl,
    emonMint: EMON_MINT.toBase58(),
  };
}
